import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { humanize, titleize } from 'underscore.string';
import Tooltip from 'components/v2/Tooltip';
import Badge from 'components/v2/Badge';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';
import { getEquipmentBadgeColor } from 'helpers/equipment';
const TOOLTIPS = {
    unavailable: 'EQUIPMENT_ATTACH_NOTE',
};
function renderIcon(params) {
    const { size, type } = params;
    return (React.createElement(ExclamationIcon, { size: size, type: type, className: "text-red w-5 h-5" }));
}
export default function EquipmentTable({ equipments, availableEquipment, showAvailability, shipmentId, onRemove, onAdd, }) {
    const equipmentIds = equipments.map((eq) => eq._id);
    const equipmentsToAdd = availableEquipment.filter((eq) => !equipmentIds.includes(eq._id));
    const { t } = useTranslation();
    function renderAvailableEquipment() {
        if (equipmentsToAdd.length === 0) {
            return null;
        }
        return ((React.createElement("div", { "data-testid": "div-d4e06586" },
            React.createElement("h4", { "data-testid": "h4-bf689c42", className: "" }, t('AVAILABLE_EQUIPMENT')),
            React.createElement("table", { "data-testid": "table-712c0730", className: "mt-2 default-table" },
                React.createElement("thead", { "data-testid": "thead-11355591" },
                    React.createElement("tr", { "data-testid": "tr-49c39582" },
                        React.createElement("th", { "data-testid": "th-ebedc758" }, titleize(t('TYPE'))),
                        React.createElement("th", { "data-testid": "th-f022804a" }, titleize(t('EQUIPMENT_ID'))),
                        React.createElement("th", { "data-testid": "th-24652b48" }, titleize(t('SIZE', { count: 1 }))),
                        React.createElement("th", { "data-testid": "th-7b11526c" }, "\u00A0"))),
                React.createElement("tbody", { "data-testid": "tbody-ac064d83" }, equipmentsToAdd.map((equipment, index) => (React.createElement("tr", { "data-testid": `tr-77326859-${index}`, key: equipment._id },
                    React.createElement("td", { "data-testid": `td-06e06a5c-${index}` },
                        React.createElement("div", { "data-testid": `div-38e7eaea-${index}`, className: "flex items-center space-x-2" },
                            React.createElement(Badge, { dataTestId: `Badge-f6061f00-${index}`, color: getEquipmentBadgeColor(equipment) }, humanize(equipment._equipmentType)),
                            showAvailability &&
                                equipment.status === 'unavailable' &&
                                equipment.shipment !== shipmentId && (React.createElement(Tooltip, { dataTestId: `Tooltip-2baa15af-${index}`, id: "equipment-unavailable", position: "top", body: renderIcon }, t(TOOLTIPS.unavailable))))),
                    React.createElement("td", { "data-testid": `td-babbd87e-${index}`, className: "italic" }, equipment.equipment_id),
                    React.createElement("td", { "data-testid": `td-dfa7ae60-${index}` }, equipment._equipmentSize
                        ? `${equipment._equipmentSize}'`
                        : null),
                    React.createElement("td", { "data-testid": `td-65d3777f-${index}`, className: "flex" }, equipment.canAdd && (React.createElement(AddButton, { dataTestId: `AddButton-d0e22713-${index}`, onClick: () => onAdd(equipment) }, titleize(t('ADD')))))))))))));
    }
    return ((React.createElement("div", { "data-testid": "div-45f1d701" },
        React.createElement("table", { "data-testid": "table-78a4596a", className: "default-table" },
            React.createElement("thead", { "data-testid": "thead-a966d9b9" },
                React.createElement("tr", { "data-testid": "tr-5be5a85e" },
                    React.createElement("th", { "data-testid": "th-8d9a388e" }, titleize(t('TYPE'))),
                    React.createElement("th", { "data-testid": "th-1f0d8837" }, titleize(t('EQUIPMENT_ID'))),
                    React.createElement("th", { "data-testid": "th-2f99d24e" }, titleize(t('SIZE', { count: 1 }))),
                    React.createElement("th", { "data-testid": "th-f2ade9e7" }, "\u00A0"))),
            React.createElement("tbody", { "data-testid": "tbody-5c926a7b" }, equipments.map((equipment, index) => (React.createElement("tr", { "data-testid": `tr-f14890d3-${index}`, key: equipment._id },
                React.createElement("td", { "data-testid": `td-3ffefa55-${index}` },
                    React.createElement("div", { "data-testid": `div-cf04d692-${index}`, className: "flex items-center space-x-2" },
                        React.createElement(Badge, { dataTestId: `Badge-2c5b9ad9-${index}`, color: getEquipmentBadgeColor(equipment) }, humanize(equipment._equipmentType)),
                        showAvailability &&
                            equipment.status === 'unavailable' &&
                            equipment.shipment !== shipmentId && (React.createElement(Tooltip, { dataTestId: `Tooltip-2f5e4e11-${index}`, id: "equipment-unavailable", position: "top", body: renderIcon }, t(TOOLTIPS.unavailable))))),
                React.createElement("td", { "data-testid": `td-18021409-${index}`, className: "italic" }, equipment.equipment_id),
                React.createElement("td", { "data-testid": `td-0a767043-${index}` }, equipment._equipmentSize
                    ? `${equipment._equipmentSize}'`
                    : null),
                React.createElement("td", { "data-testid": `td-09c8dead-${index}`, className: "" }, equipment.canRemove && (React.createElement(DeleteButton, { dataTestId: `DeleteButton-710f9421-${index}`, onClick: () => onRemove(equipment) })))))))),
        renderAvailableEquipment())));
}
EquipmentTable.propTypes = {
    equipments: PropTypes.array,
    availableEquipment: PropTypes.array,
    shipmentId: PropTypes.string.isRequired,
    showAvailability: PropTypes.bool,
    onAdd: PropTypes.func,
    onRemove: PropTypes.func,
};
EquipmentTable.defaultProps = {
    equipments: [],
    availableEquipment: [],
    showAvailability: false,
    onAdd: () => { },
    onRemove: () => { },
};
